import { useCallback, useEffect, useMemo, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { FileDown, Loader2, CalendarRange, Receipt, Sparkles, Copy, Check, History, Share2, MessageCircle, Mail } from "lucide-react";
import { useShareToAccountant } from "@/hooks/useShareToAccountant";
import BiSelect from "@/components/BiSelect";
import FlagsPreflightCard from "@/components/tax/FlagsPreflightCard";
import PdfPreviewSheet from "@/components/tax/PdfPreviewSheet";

const COPY_HISTORY_KEY = "bi.bas.copy_history.v1";
const HISTORY_MAX = 5;

function readHistory() {
  try {
    const raw = window.localStorage.getItem(COPY_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeHistory(items) {
  try {
    window.localStorage.setItem(COPY_HISTORY_KEY, JSON.stringify(items.slice(0, HISTORY_MAX)));
  } catch (err) {
    devLog.warn("bas history persist failed", err);
  }
}

/**
 * Big one-tap BAS worksheet download card.
 * - Auto-picks the most recently completed AU BAS quarter on mount.
 * - Renders a picker (last 4 completed quarters) and live totals for the selected quarter.
 * - "Prepare this quarter's BAS" downloads /api/export/bas-pdf?year=&q=.
 */
function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Build the picker option list — 4 fully-completed quarters + the current
 * in-progress quarter + the next upcoming quarter, sorted newest-first.
 *
 * We deterministically compute this from *the user's clock* (browser
 * `new Date()`) rather than deriving from whatever the server considered
 * "current". That way if the backend defaults change (e.g. lodgement-target
 * vs in-progress semantics) the picker still surfaces the periods a tradie
 * would expect to see today.
 *
 * AU BAS quarters (financial year aligned):
 *   Q1 Jul-Sep · Q2 Oct-Dec · Q3 Jan-Mar · Q4 Apr-Jun
 * `year` = the financial-year end (FY27 = 2027 = covering Jul-2026..Jun-2027).
 */
function quarterOptionsAroundToday() {
  const now = new Date();
  const m = now.getMonth() + 1; // 1-12
  const y = now.getFullYear();
  const fyEnd = m <= 6 ? y : y + 1;
  const curQ = m <= 3 ? 3 : m <= 6 ? 4 : m <= 9 ? 1 : 2;

  const stepBack = (yr, q) => (q === 1 ? { year: yr - 1, q: 4 } : { year: yr, q: q - 1 });
  const stepForward = (yr, q) => (q === 4 ? { year: yr + 1, q: 1 } : { year: yr, q: q + 1 });

  const opts = [];
  // Include one upcoming quarter so tradies can pre-flight planning without
  // fighting the picker on 30 Sep.
  opts.push({ ...stepForward(fyEnd, curQ), _tag: "upcoming" });
  // Current in-progress quarter — this becomes the default selection.
  opts.push({ year: fyEnd, q: curQ, _tag: "current" });
  // Four fully-completed quarters going back.
  let cur = stepBack(fyEnd, curQ);
  for (let i = 0; i < 4; i += 1) {
    opts.push({ ...cur, _tag: "past" });
    cur = stepBack(cur.year, cur.q);
  }
  return opts;
}

function quarterLabel(o) {
  const base = `Q${o.q} FY${String(o.year).slice(-2)}`;
  if (o._tag === "current") return `${base} · Current`;
  if (o._tag === "upcoming") return `${base} · Upcoming`;
  return base;
}

export default function BasOneTap() {
  const { accountantName, accountantEmail, bizName, abn, shareVia: shareToAccountant, prefetchPdf } = useShareToAccountant();
  const [current, setCurrent] = useState(null); // { year, q, label, ... }
  const [chosen, setChosen] = useState(null); // { year, q }
  const [busy, setBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null); // "g11" | "1b" | "all"
  const [history, setHistory] = useState(() => readHistory());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCfg, setPreviewCfg] = useState(null);

  const rememberCopy = useCallback((entry) => {
    setHistory((prev) => {
      const next = [entry, ...prev.filter((h) => h.id !== entry.id)].slice(0, HISTORY_MAX);
      writeHistory(next);
      return next;
    });
  }, []);

  const copyToClipboard = useCallback(async (text, key, label) => {
    try {
      // Prefer async clipboard API; fall back to legacy execCommand for older PWA contexts.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
      toast.success(`${label} copied — ${text}`);
    } catch (err) {
      devLog.warn("clipboard copy failed", err);
      toast.error("Copy failed — long-press to select instead");
    }
  }, []);

  /**
   * Build the BAS summary text for a given quarter snapshot.
   * Used by both live-quarter and history-row share flows so the wording
   * stays identical across every channel (clipboard, WhatsApp, email, native share).
   */
  const buildSummary = useCallback((snap) => {
    if (!snap) return "";
    const label = snap.label || `Q${snap.q ?? snap.quarter} FY${String(snap.year ?? "").slice(-2)}`;
    const g11 = typeof snap.g11 === "string" ? snap.g11 : Number(snap.g11_non_capital_purchases || 0).toFixed(2);
    const g1b = typeof snap.g1b === "string" ? snap.g1b : Number(snap.gst_1b_purchases || 0).toFixed(2);
    const entries = snap.entries ?? snap.entry_count ?? 0;
    const greeting = accountantName ? `Hi ${accountantName},\n\n` : "";
    // Business-first heading so accountants recognise the sender before the
    // number wall. Falls back to "BAS summary" alone when a tradie hasn't
    // set their business name yet.
    const bizLine = bizName ? `${bizName}${abn ? ` · ABN ${abn}` : ""}\n` : "";
    return (
      `${greeting}${bizLine}BAS Worksheet · ${label}\n\n` +
      `G11 Non-capital purchases: $${g11}\n` +
      `1B GST on purchases: $${g1b}\n` +
      `Entries: ${entries}\n\n` +
      `Full BAS Worksheet PDF attached (prepared with BuildIntel AI). ` +
      `Please review before lodging.`
    );
  }, [accountantName, bizName, abn]);

  /**
   * Every BAS share entry point — WhatsApp / Email / native Share, plus
   * the recent-copy history rows — now opens the in-app PDF preview
   * sheet first. The tradie sees the worksheet pages before invoking
   * any OS share sheet. Share happens from inside the preview.
   */
  // eslint-disable-next-line no-unused-vars
  const shareVia = useCallback(async (_kind, snap, historyEntry) => {
    const text = buildSummary(snap);
    const label = snap?.label || historyEntry?.label || "BAS quarter";
    const subject = bizName ? `${bizName} — BAS Worksheet ${label}` : `BAS Worksheet — ${label}`;
    const yr = snap?.year ?? historyEntry?.year;
    const qq = snap?.q ?? snap?.quarter ?? historyEntry?.q;
    if (!yr || !qq) return;
    setPreviewCfg({
      pdfUrl: `/export/bas-pdf?year=${yr}&q=${qq}&approved_only=${approvedOnly}&with_evidence=1`,
      filename: `bas-worksheet-${label.replace(/\s+/g, "-").toLowerCase()}.pdf`,
      title: `BAS Worksheet · ${label}`,
      description: "Review the worksheet before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
    if (historyEntry) {
      rememberCopy({ ...historyEntry, copied_at: new Date().toISOString() });
    }
  }, [buildSummary, rememberCopy, bizName, approvedOnly]);

  const load = useCallback(async (yr, qq) => {
    setBusy(true);
    try {
      const params = yr && qq ? { year: yr, q: qq } : {};
      const { data } = await api.get("/tax/bas-quarter", { params });
      setCurrent(data);
      setChosen((prev) => prev || { year: data.year, q: data.quarter });
    } catch (err) {
      devLog.warn("bas-quarter load failed", err);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (chosen && current && (chosen.year !== current.year || chosen.q !== current.quarter)) {
      load(chosen.year, chosen.q);
    }
  }, [chosen, current, load]);

  const options = useMemo(() => quarterOptionsAroundToday(), []);

  // Warm the PDF cache as soon as the current quarter loads. When the
  // tradie later taps Share, `useShareToAccountant.shareVia` finds the
  // File in the cache and fires `navigator.share({files})` synchronously
  // (native OS share sheet). Without this, iOS Safari would lose the
  // user gesture during the fetch and the share sheet wouldn't appear.
  useEffect(() => {
    if (!current) return;
    const label = `Q${current.quarter}-FY${String(current.year).slice(-2)}`;
    prefetchPdf(
      `/export/bas-pdf?year=${current.year}&q=${current.quarter}&approved_only=${approvedOnly}&with_evidence=1`,
      `bas-worksheet-${label.toLowerCase()}.pdf`
    );
  }, [current, approvedOnly, prefetchPdf]);

  // Seed `chosen` to the CURRENT in-progress quarter immediately so the
  // picker shows a sensible default even before /tax/bas-quarter has
  // resolved. Without this the user sees whatever the backend considers
  // "current" (last-completed for lodgement) — which was the source of the
  // "can't select Q1 FY27" bug.
  useEffect(() => {
    if (chosen) return;
    const cur = options.find((o) => o._tag === "current");
    if (cur) setChosen({ year: cur.year, q: cur.q });
  }, [options, chosen]);

  // "Prepare BAS worksheet" opens the in-app PDF preview — never fires
  // a same-tab PDF or a first-tap navigator.share. Share happens from
  // inside the preview.
  const download = () => {
    if (!current) return;
    const label = current.label || `Q${current.quarter} FY${String(current.year).slice(-2)}`;
    const text = buildSummary(current);
    const subject = bizName ? `${bizName} — BAS Worksheet ${label}` : `BAS Worksheet — ${label}`;
    setPreviewCfg({
      pdfUrl: `/export/bas-pdf?year=${current.year}&q=${current.quarter}&approved_only=${approvedOnly}&with_evidence=1`,
      filename: `buildintel-bas-Q${current.quarter}-FY${String(current.year).slice(-2)}.pdf`,
      title: `BAS Worksheet · ${label}`,
      description: "Review the worksheet before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
  };
  // Suppress unused warning on the retained legacy downloader — kept
  // reachable only in case a future call-site needs raw blob download.
  // eslint-disable-next-line no-unused-vars
  const _legacyDownload = async () => {
    if (!current) return;
    setDlBusy(true);
    try {
      await downloadFile(
        `/api/export/bas-pdf?year=${current.year}&q=${current.quarter}&approved_only=${approvedOnly}`,
        `buildintel-bas-Q${current.quarter}-FY${String(current.year).slice(-2)}.pdf`
      );
      toast.success(`BAS worksheet for ${current.label} downloaded`);
    } catch (err) {
      devLog.warn("bas download failed", err);
      toast.error(err.message || "Download failed");
    } finally {
      setDlBusy(false);
    }
  };

  return (
    <div
      className="bi-surface p-4 relative overflow-hidden border border-[#FF9500]/20"
      data-testid="bas-onetap"
    >
      <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-20 bg-[#FF9500] pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[#FF9500]">
          <Sparkles className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-heading">BAS ONE-TAP</span>
        </div>
        <div className="mt-1">
          <h2 className="font-heading text-xl font-extrabold leading-tight">
            Prepare this quarter&apos;s BAS
          </h2>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs">
          <CalendarRange className="w-3.5 h-3.5 text-white/40" />
          <BiSelect
            data-testid="bas-quarter-picker"
            value={chosen ? `${chosen.year}-${chosen.q}` : ""}
            onChange={(e) => {
              const [y, q] = e.target.value.split("-").map(Number);
              setChosen({ year: y, q });
            }}
            className="bi-input h-9 flex-1 min-w-0"
          >
            {options.map((o) => (
              <option key={`${o.year}-${o.q}`} value={`${o.year}-${o.q}`}>
                {quarterLabel(o)}
              </option>
            ))}
          </BiSelect>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer select-none whitespace-nowrap shrink-0">
            <input
              type="checkbox"
              data-testid="bas-approved-only"
              checked={approvedOnly}
              onChange={(e) => setApprovedOnly(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#FF9500]"
            />
            Approved only
          </label>
        </div>

        {/* Full selected-period label — sits on its own row so it can't
            be truncated by the h2 heading beside it, and stays visible
            while scrolling because it's inside the card, not sticky. */}
        {current && (
          <div
            data-testid="bas-label"
            className="mt-2 flex items-center gap-1.5 text-[11px] text-white/60"
          >
            <span className="uppercase tracking-widest text-white/40 font-heading">
              Showing
            </span>
            <span className="font-medium text-white/85">{current.label}</span>
          </div>
        )}

        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="bi-surface-2 py-2.5 px-3 rounded-lg relative" data-testid="bas-g11">
            <div className="text-[9px] uppercase tracking-wider text-white/50">G11 Purchases</div>
            <div className="font-heading font-extrabold text-lg tabular-nums pr-6">
              {busy && !current ? "…" : money(current?.g11_non_capital_purchases)}
            </div>
            {current && (
              <button
                type="button"
                data-testid="bas-copy-g11"
                onClick={() =>
                  copyToClipboard(
                    Number(current.g11_non_capital_purchases || 0).toFixed(2),
                    "g11",
                    "G11",
                  )
                }
                className="absolute top-1 right-1 w-8 h-8 rounded-md flex items-center justify-center text-white/40 hover:text-[#FF9500] active:scale-90 transition"
                title="Copy G11 value"
              >
                {copiedKey === "g11" ? (
                  <Check className="w-3.5 h-3.5 text-[#34D399]" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>
          <div className="bi-surface-2 py-2.5 px-3 rounded-lg relative" data-testid="bas-1b">
            <div className="text-[9px] uppercase tracking-wider text-white/50">1B GST identified</div>
            <div className="font-heading font-extrabold text-lg tabular-nums text-[#FF9500] pr-6">
              {busy && !current ? "…" : money(current?.gst_1b_purchases)}
            </div>
            {current && (
              <button
                type="button"
                data-testid="bas-copy-1b"
                onClick={() =>
                  copyToClipboard(
                    Number(current.gst_1b_purchases || 0).toFixed(2),
                    "1b",
                    "1B",
                  )
                }
                className="absolute top-1 right-1 w-8 h-8 rounded-md flex items-center justify-center text-white/40 hover:text-[#FF9500] active:scale-90 transition"
                title="Copy 1B value"
              >
                {copiedKey === "1b" ? (
                  <Check className="w-3.5 h-3.5 text-[#34D399]" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>
          <div className="bi-surface-2 py-2.5 px-3 rounded-lg" data-testid="bas-entries">
            <div className="text-[9px] uppercase tracking-wider text-white/50">Entries</div>
            <div className="font-heading font-extrabold text-lg tabular-nums">
              {busy && !current ? "…" : (current?.entry_count ?? 0)}
            </div>
          </div>
        </div>

        {current && current.pending_count > 0 && approvedOnly && (
          <div className="mt-2 text-[11px] text-[#FBBF24] inline-flex items-center gap-1.5">
            <Receipt className="w-3 h-3" />
            {current.pending_count} pending {current.pending_count === 1 ? "entry" : "entries"} not
            included — approve them first, or uncheck &ldquo;Approved only&rdquo;.
          </div>
        )}

        {/* Framing line — this is a workpaper for the accountant, not a
            lodgement-ready summary. GST on sales isn't tracked in-app yet,
            so any "net" figure here is inherently incomplete. */}
        <div
          data-testid="bas-workpaper-note"
          className="mt-3 text-[11px] text-white/55 leading-snug"
        >
          Supporting workpaper for your accountant. GST on sales isn&apos;t tracked here — any net figure is indicative only and subject to full review before lodgement.
        </div>

        <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
          <button
            type="button"
            data-testid="bas-download-btn"
            onClick={download}
            disabled={dlBusy || busy || !current}
            className="h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
          >
            {dlBusy ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Building…
              </>
            ) : (
              <>
                <FileDown className="w-5 h-5" /> Prepare BAS worksheet
              </>
            )}
          </button>
          <button
            type="button"
            data-testid="bas-copy-mygov"
            disabled={!current}
            onClick={() => {
              const g11 = Number(current?.g11_non_capital_purchases || 0).toFixed(2);
              const g1b = Number(current?.gst_1b_purchases || 0).toFixed(2);
              const label = current?.label || "";
              const text = `${label}\nG11 Non-capital purchases: ${g11}\n1B GST on purchases: ${g1b}\nEntries: ${current?.entry_count ?? 0}`;
              copyToClipboard(text, "all", "BAS summary");
              rememberCopy({
                id: `${current?.year}-${current?.quarter}`,
                year: current?.year,
                q: current?.quarter,
                label,
                g11,
                g1b,
                entries: current?.entry_count ?? 0,
                copied_at: new Date().toISOString(),
              });
            }}
            className="h-14 px-4 rounded-xl bi-ghost-btn flex flex-col items-center justify-center gap-0 disabled:opacity-60"
            title="Copy G11 + 1B for myGov"
          >
            {copiedKey === "all" ? (
              <Check className="w-5 h-5 text-[#34D399]" />
            ) : (
              <Copy className="w-5 h-5" />
            )}
            <span className="text-[10px] leading-tight mt-0.5">myGov</span>
          </button>
        </div>
        <div className="mt-1 text-[10px] text-white/40 text-center">
          Tap a tile&apos;s copy icon to paste one number, or use &ldquo;myGov&rdquo; for all
          three at once.
        </div>

        {current && (
          <div className="mt-3 pt-3 border-t border-white/5" data-testid="bas-share-row">
            {/* Pack-intelligence preflight — advisory only, scoped to the
                flags that actually matter for a BAS lodgement (missing
                receipts, GST mismatches). Never hard-blocks Share. */}
            <div className="mb-2">
              <FlagsPreflightCard
                scope={["no_receipt", "gst_mismatch", "unassigned_job"]}
                testid="bas-flags-preflight"
              />
            </div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-2">
              <Share2 className="w-3 h-3" /> Send to {accountantName ? accountantName : "accountant"}
              {accountantEmail && (
                <span className="text-white/30 normal-case tracking-normal truncate">· {accountantEmail}</span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                data-testid="bas-share-whatsapp"
                onClick={() => shareVia("whatsapp", current, {
                  id: `${current.year}-${current.quarter}`,
                  year: current.year,
                  q: current.quarter,
                  label: current.label,
                  g11: Number(current.g11_non_capital_purchases || 0).toFixed(2),
                  g1b: Number(current.gst_1b_purchases || 0).toFixed(2),
                  entries: current.entry_count ?? 0,
                })}
                className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
                title="Share via WhatsApp"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                <span>WhatsApp</span>
              </button>
              <button
                type="button"
                data-testid="bas-share-email"
                onClick={() => shareVia("email", current, {
                  id: `${current.year}-${current.quarter}`,
                  year: current.year,
                  q: current.quarter,
                  label: current.label,
                  g11: Number(current.g11_non_capital_purchases || 0).toFixed(2),
                  g1b: Number(current.gst_1b_purchases || 0).toFixed(2),
                  entries: current.entry_count ?? 0,
                })}
                className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
                title="Share via Email"
              >
                <Mail className="w-4 h-4 text-[#60A5FA]" />
                <span>Email</span>
              </button>
              <button
                type="button"
                data-testid="bas-share-native"
                onClick={() => shareVia("native", current, {
                  id: `${current.year}-${current.quarter}`,
                  year: current.year,
                  q: current.quarter,
                  label: current.label,
                  g11: Number(current.g11_non_capital_purchases || 0).toFixed(2),
                  g1b: Number(current.gst_1b_purchases || 0).toFixed(2),
                  entries: current.entry_count ?? 0,
                })}
                className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
                title="Share via device"
              >
                <Share2 className="w-4 h-4 text-[#FF9500]" />
                <span>Share</span>
              </button>
            </div>
          </div>
        )}

        <PdfPreviewSheet
          open={previewOpen}
          onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreviewCfg(null); }}
          pdfUrl={previewCfg?.pdfUrl}
          filename={previewCfg?.filename}
          title={previewCfg?.title}
          description={previewCfg?.description}
          shareText={previewCfg?.shareText}
          shareSubject={previewCfg?.shareSubject}
          testid="bas-preview"
        />

        {history.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/5" data-testid="bas-copy-history">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-2">
              <History className="w-3 h-3" /> Recent myGov copies
            </div>
            <div className="space-y-1">
              {history.map((h) => (
                <div
                  key={`${h.id}-${h.copied_at}`}
                  data-testid={`bas-history-${h.id}`}
                  className="flex items-center justify-between gap-2 rounded-lg py-1.5 px-2 bg-white/[0.02] hover:bg-white/[0.06] transition"
                >
                  <button
                    type="button"
                    data-testid={`bas-history-copy-${h.id}`}
                    onClick={() => {
                      const text = buildSummary(h);
                      copyToClipboard(text, "all", `${h.label} BAS`);
                      rememberCopy({ ...h, copied_at: new Date().toISOString() });
                    }}
                    className="min-w-0 text-left flex-1 flex items-center gap-2"
                    title="Tap to copy again"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-white/90 truncate">{h.label}</div>
                      <div className="text-[10px] text-white/40 tabular-nums">
                        G11 ${h.g11} · 1B ${h.g1b}
                      </div>
                    </div>
                    <Copy className="w-3.5 h-3.5 text-white/40 shrink-0" />
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      data-testid={`bas-history-whatsapp-${h.id}`}
                      onClick={() => shareVia("whatsapp", h, h)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-white/40 hover:text-[#25D366] active:scale-90 transition"
                      title="Share via WhatsApp"
                      aria-label="Share via WhatsApp"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`bas-history-email-${h.id}`}
                      onClick={() => shareVia("email", h, h)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-white/40 hover:text-[#60A5FA] active:scale-90 transition"
                      title="Share via Email"
                      aria-label="Share via Email"
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
